import type { StudioForm } from '../studio/studio-api';
import { record, rows, text } from '../studio/studio-content';
import { PROMPT_MAX } from '../studio/prompt-limit';
export type PhotoMode = 'generate' | 'style' | 'edit' | 'avatar' | 'restore' | 'colorize';
export interface PhotoPolicy {
  policy_version: string;
  mode: PhotoMode;
  formats: string[];
  counts: number[];
  styles: Array<{
    id: string;
    name: string;
    look: string;
    photos: string[];
    examples: string[];
    themes: string[];
    description: string;
    popularity: number;
    isNew: boolean;
    hit: boolean;
  }>;
  default_style_id?: string;
  reference_available: boolean;
  people_available: boolean;
  max_people: number;
  description_min: number;
  description_max: number;
  upload_max_bytes: number;
  /** Пример «Было · Стало» в пустой рамке пресета (§1.3) — только если сервер его публикует */
  example?: unknown;
  /** Своё описание рамки пресета («Лицо крупно, хороший свет») */
  upload_hint?: string;
  /** Можно ли при этом стиле прийти без описания (рисованный стиль, свой референс) */
  improvement_available?: boolean;
}
export const photoModeLabels: Record<PhotoMode, string> = {
  generate: 'По описанию',
  style: 'В новом стиле',
  edit: 'Изменить фото',
  avatar: 'Фото на аватарку',
  restore: 'Восстановить фото',
  colorize: 'Раскрасить чёрно-белое',
};
export function photoPolicy(form: StudioForm): PhotoPolicy | null {
  const p = record(record(form.constraints).photo);
  if (p.policy_version !== 'photo-n8n-v1' || !Object.hasOwn(photoModeLabels, String(p.mode))) return null;
  return {
    ...p,
    mode: p.mode as PhotoMode,
    policy_version: 'photo-n8n-v1',
    formats: Array.isArray(p.formats) ? p.formats.filter(x => typeof x === 'string') : [],
    counts: Array.isArray(p.counts) ? p.counts.filter(x => Number.isInteger(x) && [1, 2, 4].includes(x)) : [],
    styles: rows(p.styles).flatMap(s =>
      typeof s.id === 'string' || typeof s.id === 'number'
        ? [
            {
              id: String(s.id),
              name: text(s.name),
              look: text(s.look),
              photos: Array.isArray(s.photos) ? s.photos.filter(x => typeof x === 'string') : [],
              examples: Array.isArray(s.examples) ? s.examples.filter(x => typeof x === 'string') : [],
              themes: Array.isArray(s.themes) ? s.themes.filter(x => typeof x === 'string') : [],
              description: text(s.description),
              popularity: Number(s.popularity) || 0,
              isNew: s.new === true,
              hit: s.hit === true,
            },
          ]
        : [],
    ),
    reference_available: p.reference_available === true,
    people_available: p.people_available === true,
    max_people: Math.max(0, Math.min(4, Number(p.max_people) || 0)),
    description_min: typeof p.description_min_length === 'number' ? p.description_min_length : 10,
    description_max: Math.min(PROMPT_MAX, Number(p.description_max_length) || PROMPT_MAX),
    upload_max_bytes: Number(p.upload_max_bytes) || 10 * 1024 * 1024,
    example: p.example,
    upload_hint: text(p.upload_hint) || undefined,
    improvement_available: p.improvement_available === true,
  };
}
export const photoLabels: Record<string, string> = {
  photo: 'Фото',
  description: 'Описание',
  style: 'Стиль',
  reference: 'Свой референс',
  format: 'Формат',
  count: 'Сколько фото',
  output_type: 'Формат файла',
  crop_x: 'Кадр по горизонтали',
  crop_y: 'Кадр по вертикали',
  zoom: 'Приближение',
};
export const photoValues: Record<string, string> = {
  none: 'Без стиля',
  auto: 'Как у фото',
  '1:1': 'Квадрат',
  '3:4': 'Портрет',
  '4:5': 'Пост',
  '9:16': 'Сторис',
  '16:9': 'Горизонтальное',
  '4:3': 'Альбомное',
  jpeg: 'JPEG',
  png: 'PNG',
};
export function photoMissing(
  form: StudioForm,
  inputs: Record<string, string>,
  rights: boolean,
  uploadIds: Record<string, string> = {},
): string | null {
  const policy = photoPolicy(form);
  if (
    form.fields.some(f => f.upload && (f.required || inputs[f.key] || (uploadIds[f.key] && !(f.key === 'reference' && inputs.style)))) &&
    !rights
  )
    return 'Подтвердите права на материалы';
  if (policy) {
    if (policy.mode !== 'generate' && !inputs.photo) return 'Загрузите фото';
    const needsDescription = photoDescriptionOnForm(policy, inputs) && !(policy.mode !== 'generate' && inputs.reference);
    if (needsDescription && Array.from((inputs.description ?? '').trim()).length < policy.description_min)
      return 'Добавьте описание, от 10 знаков';
    if (Array.from(inputs.description ?? '').length > policy.description_max)
      return `Описание длиннее ${policy.description_max} знаков`;
    if (uploadIds.reference && !inputs.reference && (!inputs.style || inputs.style === 'none')) return 'Проверяем картинку';
    if (policy.mode === 'style' && !inputs.reference && (!inputs.style || inputs.style === 'none'))
      return 'Выберите стиль или свой референс';
    const people = Array.from({ length: policy.max_people }, (_, index) => inputs[`cast_${index + 1}`]).filter(Boolean);
    if (new Set(people).size !== people.length) return 'Выберите разных людей в кадре';
    if (inputs.reference && !policy.reference_available) return 'Свой референс сейчас недоступен';
  }
  return form.fields.some(field => field.required && !inputs[field.key]?.trim()) ? 'Заполните обязательные поля' : null;
}
/** Описание стоит на форме (§2.3): «По описанию» — «Описание», «Без стиля» — «Что поменять»; со стилем — слот карточки «Что изменить». */
export function photoDescriptionOnForm(policy: PhotoPolicy, inputs: Record<string, string>): boolean {
  return policy.mode === 'generate' || (policy.mode === 'edit' && (!inputs.style || inputs.style === 'none'));
}
/** Подпись рамки по пресету (§2.1, таблица пресетов). */
export function photoUploadHint(policy: PhotoPolicy): string {
  return (
    policy.upload_hint ??
    ({ avatar: 'Лицо крупно, хороший свет', restore: 'Старый снимок или скан', colorize: 'Чёрно-белый снимок или скан' } as Record<string, string>)[policy.mode] ??
    'Селфи, портрет или любой снимок'
  );
}
/** Чипы описания шаблона (CHIPS.edit и CHIPS.ph, assets/bot-photo.js): подпись чипа и полный текст, который он вставляет. */
export const photoChangeChips = [
  ['Сменить фон', 'Замени фон на ночной город'],
  ['Убрать лишнее', 'Убери людей и предметы на заднем плане'],
  ['Добавить очки', 'Добавь солнцезащитные очки'],
  ['Причёска', 'Сделай короткую стрижку'],
  ['Улыбка', 'Сделай лёгкую улыбку'],
] as const;
export const photoTweakChips = [
  ['Улыбка', 'Лёгкая естественная улыбка'],
  ['Деловой образ', 'Деловой костюм, нейтральный фон'],
  ['Летний фон', 'Летний парк на заднем плане'],
] as const;
/** Подсказки поля описания по виду (K.place фото, assets/bot-photo.js): tx — «По описанию», edit — «Без стиля», ph — добавка к фото. */
export const photoPlaceholders = {
  tx: 'Что должно быть на фото? Например: девушка с зонтом на мокрой улице, вечер, огни витрин',
  edit: 'Что поменять на фото? Например: замени фон на ночной город, добавь очки',
  ph: 'Что добавить или поменять? Например: деловой костюм, лёгкая улыбка',
} as const;
/** Порядок тем «Всех стилей» из документа экрана (photo.themes; как у Block() сервера — settings.photo, затем корень). */
export function photoThemeOrder(screen: unknown, block = 'photo'): string[] {
  const document = record(screen);
  const list = record(document[block]).themes ?? record(record(document.settings)[block]).themes ?? document.themes;
  return Array.isArray(list) ? list.filter((item): item is string => typeof item === 'string') : [];
}
/** Чипы тем листа «Все стили»: только темы, которые есть у стилей, в порядке документа экрана; неизвестные — в конце,
    без порядка — по первому появлению. У пресета со своими стилями (avatar) тем нет — как у шаблона (bot-photo.js, gridSheet themes). */
export function photoThemes(policy: Pick<PhotoPolicy, 'mode' | 'styles'>, order: readonly string[] = []): string[] {
  if (policy.mode === 'avatar') return [];
  const present = [...new Set(policy.styles.flatMap(style => style.themes))];
  if (!order.length) return present;
  const rank = (theme: string) => (order.includes(theme) ? order.indexOf(theme) : Number.MAX_SAFE_INTEGER);
  return present
    .map((theme, index) => [theme, index] as const)
    .sort((a, b) => rank(a[0]) - rank(b[0]) || a[1] - b[1])
    .map(([theme]) => theme);
}
export function choosePhotoStyle(inputs: Record<string, string>, style: string) {
  const next: Record<string, string> = { ...inputs, style };
  delete next.reference;
  return next;
}
